import { useState, useCallback } from 'react'

/**
 * Hook for handling image paste functionality in the chat.
 * Converts pasted images to base64 and manages the image list.
 */
export function useImagePaste() {
  const [pastedImages, setPastedImages] = useState<string[]>([])

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items

    const imageFiles: File[] = []
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) imageFiles.push(file)
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault()

      // Convert images to base64
      const base64Images = await Promise.all(
        imageFiles.map(
          (file) =>
            new Promise<string>((resolve) => {
              const reader = new FileReader()
              reader.onloadend = () => {
                resolve(reader.result as string)
              }
              reader.readAsDataURL(file)
            })
        )
      )

      setPastedImages((previous) => [...previous, ...base64Images])
    }
  }, [])

  const removeImage = useCallback((index: number) => {
    setPastedImages((previous) => previous.filter((_, i) => i !== index))
  }, [])

  const clearImages = useCallback(() => {
    setPastedImages([])
  }, [])

  return {
    pastedImages,
    handlePaste,
    removeImage,
    clearImages
  }
}